/**
 * Bloco "Cronograma de pagamento" do Detalhe do Pedido (Tarefa F2).
 *
 * Lê GET /api/pedidos/{id}/cronograma-pagamento (Tarefa B2): parcelas da
 * condicao de pagamento copiada do orcamento na conversao, com data prevista
 * calculada das datas REAIS do processo (aprovação = criação do pedido,
 * início da produção, entrega, instalação concluída). Âncora que ainda não
 * aconteceu vem sem data — Pill neutra "aguardando evento".
 *
 * Quando o pedido NÃO tem condição, o bloco não some mais: oferece o
 * seletor para o vendedor definir. Isso é o que destrava o Contas a Receber
 * no motor CASCATA — nele a forma de pagamento é texto livre no orçamento e
 * não dá para derivar parcelas, então quem escolhe é uma pessoa. (No DIVISOR
 * a condição já vem da precificação e o seletor nem aparece.)
 * Melhor esforço: falha da API não quebra o Detalhe.
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../components/AsyncButton";
import { useAuth } from "../../context/AuthContext";
import {
  ANCORA_PARCELA_LABEL, listarCondicoesPagamento,
} from "../../services/financeiro";
import { atualizarPedido, obterCronogramaPagamento } from "../../services/pedidos";
import { Select } from "../Orcamentos/styles";
import { fmtData, fmtMoeda } from "../Orcamentos/helpers";
import { Pill } from "./styles";

// data_prevista chega como date-only ("YYYY-MM-DD"). `new Date("YYYY-MM-DD")`
// parseia como MEIA-NOITE UTC e no fuso de SP vira o dia anterior — formata
// direto da string para nao deslocar o dia.
function fmtDataPrevista(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return fmtData(iso);
}

export default function CronogramaPagamento({ pedidoId, podeEditar = true }) {
  const { request } = useAuth();
  const [payload, setPayload] = useState(null);
  const [condicoes, setCondicoes] = useState([]);
  const [escolhida, setEscolhida] = useState("");
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    let ativo = true;
    obterCronogramaPagamento(request, pedidoId, { silentError: true })
      .then((p) => { if (ativo) setPayload(p); })
      .catch(() => { if (ativo) setPayload(null); });
    return () => { ativo = false; };
  }, [request, pedidoId, recarga]);

  // Só busca as condições quando o pedido ainda não tem uma.
  const semCondicao = !payload?.condicao_pagamento;
  useEffect(() => {
    let ativo = true;
    if (!semCondicao || !podeEditar) return undefined;
    listarCondicoesPagamento(request, { ativo: true }, { silentError: true })
      .then((r) => {
        if (!ativo) return;
        const lista = Array.isArray(r) ? r : r?.results || [];
        setCondicoes(lista);
        // Favorita da unidade entra pré-selecionada — é só sugestão visual,
        // nada é gravado sem o vendedor confirmar.
        const favorita = lista.find((c) => c.favorita);
        if (favorita) setEscolhida(String(favorita.id));
      })
      .catch(() => { if (ativo) setCondicoes([]); });
    return () => { ativo = false; };
  }, [request, semCondicao, podeEditar]);

  const definirCondicao = async () => {
    if (!escolhida) throw new Error("Escolha a condição de pagamento.");
    await atualizarPedido(request, pedidoId, { condicao_pagamento: escolhida });
    setRecarga((n) => n + 1);
  };

  if (semCondicao) {
    if (!podeEditar) return null;
    return (
      <div className="block">
        <div className="block__head">
          <div className="block__title">Cronograma de pagamento</div>
        </div>
        <div style={{ padding: "12px 4px", display: "grid", gap: 10 }}>
          <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 13 }}>
            Este pedido ainda não tem condição de pagamento — por isso nada
            aparece em Contas a Receber. Escolha a condição para gerar as
            parcelas com as datas reais do processo.
          </p>
          {condicoes.length === 0 ? (
            <p style={{ margin: 0, fontSize: 12, color: "var(--ink-3)" }}>
              Nenhuma condição cadastrada. Cadastre em Financeiro › Cadastros ›
              Condições de pagamento.
            </p>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Select
                value={escolhida}
                onChange={(e) => setEscolhida(e.target.value)}
                style={{ maxWidth: 380 }}
              >
                <option value="">— escolha a condição —</option>
                {condicoes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}{c.favorita ? " ★" : ""}
                  </option>
                ))}
              </Select>
              <AsyncButton
                variant="primary"
                onClick={definirCondicao}
                successMessage="Parcelas geradas em Contas a Receber."
              >
                Gerar parcelas
              </AsyncButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  const parcelas = payload.parcelas || [];

  return (
    <div className="block">
      <div className="block__head">
        <div className="block__title">
          Cronograma de pagamento{" "}
          <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
            · {payload.condicao_pagamento.nome}
            {" · "}
            {payload.condicao_pagamento.contagem_dias === "UTEIS"
              ? "dias úteis"
              : "dias corridos"}
          </span>
        </div>
      </div>
      <div className="block__body" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="dtable">
            <colgroup>
              <col style={{ width: 64 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: "22%" }} />
              <col />
              <col style={{ width: "24%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>#</th>
                <th className="num" style={{ textAlign: "right" }}>%</th>
                <th className="num" style={{ textAlign: "right" }}>Valor</th>
                <th>Âncora</th>
                <th>Data prevista</th>
              </tr>
            </thead>
            <tbody>
              {parcelas.map((p) => (
                <tr key={p.ordem}>
                  <td className="mono">{p.ordem}ª</td>
                  <td className="num mono" style={{ textAlign: "right" }}>
                    {Number(p.percentual)}%
                  </td>
                  <td className="num mono" style={{ textAlign: "right" }}>
                    {fmtMoeda(p.valor)}
                  </td>
                  <td title={p.descricao || undefined}>
                    {ANCORA_PARCELA_LABEL[p.ancora] || p.ancora}
                    {p.descricao ? (
                      <span className="tiny muted"> · {p.descricao}</span>
                    ) : null}
                  </td>
                  <td>
                    {p.data_prevista ? (
                      <span className="mono">{fmtDataPrevista(p.data_prevista)}</span>
                    ) : (
                      <Pill $tone="neutral">aguardando evento</Pill>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
